/**
 * [BL] Custom tracking events — the things Shopify's standard events don't cover (see
 * _internal/docs/tracking.md). Each is published with `Shopify.analytics.publish(name, payload)`, which
 * reaches CUSTOM pixels only (Settings → Customer events); app pixels (Facebook, Google, Pinterest) never
 * see them, so a custom pixel has to subscribe and forward them. Nothing here talks to an ad platform.
 *
 *   bl_product_viewed  a product seen without a page load:
 *                        context 'quick_add'       quick add opened on it (e.g. a charm on the Keepsake Necklace)
 *                        context 'variant_change'  another variant chosen (page product or in quick add),
 *                                                  once per variant per page view
 *   bl_added_to_set    an add-on held in "Your set" (not yet in the cart; Shopify's
 *                      product_added_to_cart fires for it when the set is added)
 *   bl_quick_add_opened  the quick add modal shown (assets/quick-add.js `bl-quick-add:opened`)
 *   bl_quick_add_added   quick add put its product in the cart (mode 'cart') or in "Your set" (mode 'set')
 *                        Both add: quickAddId (pairs an add with its open), source ('product_card' |
 *                        'addon_tile' | 'set_edit' | 'hotspot' | 'other'), mode ('cart' | 'set'), layout
 *                        ('preview_led' | 'classic' | 'none': the personaliser layout seen), personalisable;
 *                        added also has quantity and personalised (it carries engraving properties).
 *   bl_lead            an email sign-up: the theme's newsletter form, or a Klaviyo form
 *   bl_saved_product   a product hearted (assets/bl-wishlist.js); same payload shape, context 'wishlist'
 *
 * Payload (all events): { context, product: { id, title, handle }, variant: { id, title, sku, price,
 * compareAtPrice }, currency, value, url }. Prices are numbers in the shop's currency unit (72, not 7200).
 * The same payload is also dispatched on `document` as `bl:track` ({ name, payload }) for debugging.
 */

/** @param {string} name @param {Record<string, unknown>} payload */
function publish(name, payload) {
  try {
    /** @type {any} */ (window).Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    console.error('[bl-tracking]', error);
  }
  document.dispatchEvent(new CustomEvent('bl:track', { detail: { name, payload } }));
}

const currency = () => /** @type {any} */ (window).Shopify?.currency?.active || '';

/** Variant JSON prices are in cents. @param {unknown} cents */
const amount = (cents) => (Number.isFinite(Number(cents)) && cents !== null && cents !== '' ? Number(cents) / 100 : null);

/**
 * @param {{ id?: unknown, title?: unknown, handle?: unknown } | null | undefined} product
 * @param {any} variant - Shopify variant JSON (id, title, sku, price, compare_at_price)
 * @param {string} context
 */
function productPayload(product, variant, context) {
  const price = amount(variant?.price);
  return {
    context,
    product: { id: product?.id ?? null, title: product?.title ?? '', handle: product?.handle ?? '' },
    variant: {
      id: variant?.id ?? null,
      title: variant?.title ?? '',
      sku: variant?.sku ?? '',
      price,
      compareAtPrice: amount(variant?.compare_at_price),
    },
    currency: currency(),
    value: price,
    url: window.location.href,
  };
}

/** The product a `product-component` (or anything carrying view-event-payload) is about. @param {Element | null} scope */
function productOf(scope) {
  try {
    const raw = scope?.closest('[view-event-payload]')?.getAttribute('view-event-payload');
    return raw ? JSON.parse(raw).product : null;
  } catch {
    return null;
  }
}

/** The selected variant's JSON, which the theme's variant picker keeps in the page. @param {Element | null} scope */
function selectedVariantOf(scope) {
  try {
    const script = scope?.querySelector('variant-picker script[type="application/json"]');
    return script?.textContent ? JSON.parse(script.textContent) : null;
  } catch {
    return null;
  }
}

const inQuickAdd = (/** @type {Element} */ element) => Boolean(element.closest('#quick-add-modal-content, .quick-add-modal'));

// --- Product views ------------------------------------------------------------------------------------

/** Variants already reported on this page view, per context (the page's first variant is Shopify's own product_viewed). */
const seen = new Set();
{
  const first = selectedVariantOf(document.querySelector('.product-information'));
  if (first?.id) seen.add(`variant_change:${first.id}`);
}

// Quick add opened: the theme announces the modal's product (shopify:product:view), which Shopify does
// not pass on to pixels.
document.addEventListener(
  'shopify:product:view',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !inQuickAdd(target)) return;
    // Let the modal's content settle (the variant JSON is morphed in with it).
    requestAnimationFrame(() => {
      const scope = document.getElementById('quick-add-modal-content');
      const variant = selectedVariantOf(scope);
      if (variant?.id) seen.add(`quick_add:${variant.id}`);
      publish('bl_product_viewed', productPayload(productOf(scope) || productOf(target), variant, 'quick_add'));
    });
  },
  true
);

// A variant chosen: on the product page, or inside quick add.
document.addEventListener(
  'shopify:product:select',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const promise = /** @type {any} */ (event).promise;
    if (!target || !promise?.then) return;
    const quickAdd = inQuickAdd(target);
    if (!quickAdd && !target.closest('.product-information')) return; // product cards etc.
    promise
      .then((/** @type {any} */ result) => {
        const variant = result?.detail?.resource;
        if (!variant?.id) return;
        const key = `${quickAdd ? 'quick_add' : 'variant_change'}:${variant.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        publish('bl_product_viewed', productPayload(productOf(target), variant, 'variant_change'));
      })
      .catch(() => {});
  },
  true
);

// --- Your set -----------------------------------------------------------------------------------------

document.addEventListener('bl-tray:add', (event) => {
  const item = /** @type {CustomEvent} */ (event).detail?.item;
  if (!item) return;
  const price = Number.isFinite(Number(item.priceCents)) ? Number(item.priceCents) / 100 : null;
  publish('bl_added_to_set', {
    context: 'set',
    product: { id: item.productId ?? null, title: item.title ?? '', handle: '' },
    variant: {
      id: item.variantId ? Number(item.variantId) : null,
      title: item.subtitle ?? '',
      sku: '',
      price,
      compareAtPrice: Number.isFinite(Number(item.compareAtCents)) ? Number(item.compareAtCents) / 100 : null,
    },
    quantity: item.quantity || 1,
    personalised: Object.keys(item.properties || {}).length > 0,
    currency: currency(),
    value: price,
    url: window.location.href,
  });
});

// --- Quick add opened / added ------------------------------------------------------------------------

/** @param {Event} event @param {string} context */
function quickAddPayload(event, context) {
  const detail = /** @type {CustomEvent} */ (event).detail || {};
  const scope = document.getElementById('quick-add-modal-content');
  return {
    ...productPayload(productOf(scope), selectedVariantOf(scope), context),
    quickAddId: detail.quickAddId ?? '',
    source: detail.source ?? 'other',
    mode: detail.mode ?? 'cart',
    layout: detail.layout ?? 'none',
    personalisable: Boolean(detail.personalisable),
  };
}

document.addEventListener('bl-quick-add:opened', (event) => {
  publish('bl_quick_add_opened', quickAddPayload(event, 'quick_add'));
});

document.addEventListener('bl-quick-add:added', (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail || {};
  const payload = quickAddPayload(event, 'quick_add');
  const quantity = Number(detail.quantity) || 1;
  publish('bl_quick_add_added', {
    ...payload,
    quantity,
    personalised: Boolean(detail.personalised),
    value: payload.value === null ? null : payload.value * quantity,
  });
});

// --- Wishlist -------------------------------------------------------------------------------------

document.addEventListener('bl-wishlist:added', (event) => {
  const { handle, product = {} } = /** @type {CustomEvent} */ (event).detail || {};
  if (!handle) return;
  const price = amount(product.price);
  publish('bl_saved_product', {
    context: 'wishlist',
    product: { id: product.productId ? Number(product.productId) : null, title: product.title ?? '', handle },
    variant: { id: product.variantId ? Number(product.variantId) : null, title: '', sku: '', price, compareAtPrice: null },
    currency: currency(),
    value: price,
    url: window.location.href,
  });
});

// --- Leads --------------------------------------------------------------------------------------------

// The theme's newsletter form reloads the page with ?customer_posted=true on success.
try {
  const params = new URLSearchParams(window.location.search);
  const key = 'bl-lead-reported';
  if (params.get('customer_posted') === 'true' && !sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1');
    publish('bl_lead', { context: 'newsletter', url: window.location.href });
  }
} catch {
  // Storage blocked: skip rather than risk reporting the same sign-up on every refresh.
}

// Klaviyo forms announce themselves on window.
window.addEventListener('klaviyoForms', (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (detail?.type !== 'submit') return;
  publish('bl_lead', { context: 'klaviyo_form', formId: detail.formId ?? null, url: window.location.href });
});
